/**
 * Creator Profile Page
 * Displays creator stats, strategies, and reviews
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getCreator,
  getRatingStars,
  formatPrice,
  type CreatorProfile as CreatorProfileType,
  type CreatorReview,
  type MarketplaceStrategy,
} from '../services/marketplaceApi'
import LoadingSpinner from '../components/LoadingSpinner'

// ============================================================================
// Components
// ============================================================================

const StarRating = ({ rating, size = 16, showCount = false, count = 0 }: { 
  rating: number; 
  size?: number; 
  showCount?: boolean;
  count?: number;
}) => {
  const { full, half, empty } = getRatingStars(rating)
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => (
        <svg key={`full-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#f59e0b">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {half && (
        <svg width={size} height={size} viewBox="0 0 20 20">
          <defs>
            <linearGradient id={`half-${Math.random()}`}>
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#e5e7eb" />
            </linearGradient>
          </defs>
          <path fill="url(#half)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
      {[...Array(empty)].map((_, i) => (
        <svg key={`empty-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#e5e7eb">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {showCount && <span className="ml-1 text-sm text-gray-400">({count})</span>}
    </div>
  )
}

const MetricCard = ({ label, value, icon }: { label: string; value: string | number; icon: string }) => (
  <div className="bg-gray-800/50 rounded-xl p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-lg">
      {icon}
    </div>
    <div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-gray-400">{label}</div>
    </div>
  </div>
)

const StrategyCardMini = ({ strategy, onClick }: { 
  strategy: MarketplaceStrategy; 
  onClick: () => void 
}) => {
  const returnColor = strategy.metrics.total_return >= 0 ? 'text-green-400' : 'text-red-400'
  return (
    <div
      onClick={onClick}
      className="bg-gray-800/50 rounded-xl p-4 cursor-pointer hover:bg-gray-750 transition-all hover:scale-[1.01]"
    >
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-semibold text-white line-clamp-1">{strategy.name}</h4>
        <span className={`text-sm font-medium ${returnColor}`}>
          {strategy.metrics.total_return >= 0 ? '+' : ''}{strategy.metrics.total_return.toFixed(1)}%
        </span>
      </div>
      <div className="flex items-center gap-3 text-sm text-gray-400">
        <StarRating rating={strategy.avg_rating} size={12} />
        <span>👁 {strategy.view_count}</span>
        <span>📋 {strategy.clone_count}</span>
      </div>
    </div>
  )
}

const ReviewCard = ({ review }: { review: CreatorReview }) => (
  <div className="bg-gray-800/50 rounded-xl p-4">
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-white">{review.username}</span>
          {review.is_verified_purchase && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Verified</span>
          )}
        </div>
        <div className="text-sm text-gray-400">on {review.strategy_name}</div>
      </div>
      <StarRating rating={review.rating} size={14} />
    </div>
    {review.review && <p className="text-gray-300 text-sm mb-2">{review.review}</p>}
    <div className="flex gap-4 text-sm">
      {review.pros.length > 0 && (
        <div>
          <span className="text-green-400">✓</span> {review.pros.join(', ')}
        </div>
      )}
      {review.cons.length > 0 && (
        <div>
          <span className="text-red-400">✗</span> {review.cons.join(', ')}
        </div>
      )}
    </div>
    <div className="text-xs text-gray-500 mt-2">
      {new Date(review.created_at).toLocaleDateString()}
    </div>
  </div>
)

// ============================================================================
// Main Component
// ============================================================================

export default function CreatorProfile() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  
  const [profile, setProfile] = useState<CreatorProfileType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'strategies' | 'reviews'>('strategies')

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return
      
      setLoading(true)
      setError(null)
      
      try {
        const response = await getCreatorProfile(userId)
        if (response.success && response.data) {
          setProfile(response.data as CreatorProfileType)
        } else {
          setError('Creator not found')
        }
      } catch (err) {
        setError('Failed to load creator profile')
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [userId])

  const handleStrategyClick = (strategy: MarketplaceStrategy) => {
    navigate(`/marketplace?strategy=${strategy.strategy_id}`)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-white mb-2">{error || 'Creator not found'}</h2>
          <button
            onClick={() => navigate('/marketplace')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    )
  }

  const joinedDate = new Date(profile.joined_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long'
  })

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/20 to-purple-500/20 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/marketplace')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <span>←</span> Back to Marketplace
          </button>
          
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-4xl font-bold text-white">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.username} className="w-full h-full rounded-full object-cover" />
              ) : (
                profile.username.charAt(0).toUpperCase()
              )}
              {profile.is_verified && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-sm">
                  ✓
                </div>
              )}
            </div>
            
            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-white">{profile.username}</h1>
                {profile.is_verified && (
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full font-medium">
                    ✓ Verified Creator
                  </span>
                )}
              </div>
              
              {profile.bio && (
                <p className="text-gray-300 mb-4 max-w-2xl">{profile.bio}</p>
              )}
              
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>📅 Joined {joinedDate}</span>
                {Object.keys(profile.social_links).length > 0 && (
                  <div className="flex items-center gap-2">
                    {profile.social_links.twitter && (
                      <a href={profile.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-white">𝕏</a>
                    )}
                    {profile.social_links.website && (
                      <a href={profile.social_links.website} target="_blank" rel="noopener noreferrer" className="hover:text-white">🌐</a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard 
            label="Strategies" 
            value={profile.total_strategies} 
            icon="📊" 
          />
          <MetricCard 
            label="Total Clones" 
            value={profile.total_clones.toLocaleString()} 
            icon="📋" 
          />
          <MetricCard 
            label="Avg Rating" 
            value={`${profile.avg_rating.toFixed(1)} ⭐`} 
            icon="⭐" 
          />
          <MetricCard 
            label="Reviews" 
            value={profile.total_ratings} 
            icon="💬" 
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex gap-1 border-b border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('strategies')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'strategies'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Strategies ({profile.strategies?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'reviews'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Reviews ({profile.recent_ratings?.length || 0})
          </button>
        </div>

        {/* Strategies Tab */}
        {activeTab === 'strategies' && (
          <div className="pb-8">
            {profile.strategies && profile.strategies.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {profile.strategies.map(strategy => (
                  <StrategyCardMini
                    key={strategy.strategy_id}
                    strategy={strategy}
                    onClick={() => handleStrategyClick(strategy)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <div className="text-4xl mb-4">📭</div>
                <p>No strategies published yet</p>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="pb-8">
            {profile.recent_ratings && profile.recent_ratings.length > 0 ? (
              <div className="space-y-4">
                {profile.recent_ratings.map(review => (
                  <ReviewCard key={review.rating_id} review={review} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <div className="text-4xl mb-4">💬</div>
                <p>No reviews yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
